import { Cookie, FileLock, Scale, ShieldCheck } from "lucide-react";

export const legalCards = [
  {
    id: "privacy-policy",
    title: "Privacy Policy Generator",
    eyebrow: "UK GDPR / GDPR",
    description: "Draft a UK GDPR–compliant privacy notice covering data controller details, processing purposes, lawful bases, retention, and data subject rights.",
    path: "/legal/privacy-policy",
    action: "Generate policy",
    Icon: ShieldCheck,
  },
  {
    id: "cookie-policy",
    title: "Cookie Policy Generator",
    eyebrow: "PECR + GDPR",
    description: "Document the cookies your site uses, by category, with retention and purposes. Pair with your consent banner.",
    path: "/legal/cookie-policy",
    action: "Generate cookie policy",
    Icon: Cookie,
  },
  {
    id: "terms-of-service",
    title: "Terms of Service Generator",
    eyebrow: "Website / SaaS",
    description: "Standard terms for a UK SaaS or service website covering use, payment, IP, liability, and termination.",
    path: "/legal/terms-of-service",
    action: "Generate terms",
    Icon: Scale,
  },
  {
    id: "dpa",
    title: "Data Processing Agreement (DPA)",
    eyebrow: "Article 28",
    description: "Article 28 GDPR–style processor agreement with subject matter, duration, processing nature, sub-processors, and Annex A.",
    path: "/legal/dpa",
    action: "Generate DPA",
    Icon: FileLock,
  },
];

export const defaultPrivacyPolicy = () => ({
  websiteName: "Northstar Studio",
  websiteUrl: "https://northstar.example.co.uk",
  controllerName: "Northstar Studio Ltd",
  controllerNumber: "12345678",
  controllerAddress: "1 Practical Lane, London EC1A 1AA",
  contactEmail: "privacy@example.co.uk",
  dpoContact: "Not appointed (no statutory requirement)",
  effectiveDate: new Date().toISOString().slice(0, 10),
  dataCollected: "Name, email, billing address, payment details (processed via Stripe), IP address, browser metadata, and any content you submit through forms.",
  purposes: "To deliver the service you have asked for, manage your account, take payment, send transactional emails, respond to support requests, and (with consent) send marketing communications.",
  lawfulBases: "Contract performance for service delivery; legitimate interests for security, analytics, and product improvement; consent for marketing emails and non-essential cookies; legal obligation for tax and accounting records.",
  recipients: "Stripe (payments), Postmark (transactional email), AWS (hosting), our accountants, and HMRC where legally required.",
  internationalTransfers: "Where personal data is transferred outside the UK or EEA, we rely on UK International Data Transfer Agreements or adequacy decisions to protect your data.",
  retention: "Account data: for the life of your account plus 6 years for tax records. Marketing: until you unsubscribe. Support tickets: 24 months after closure.",
  rights: "Access, rectification, erasure, restriction, objection, portability, and the right to withdraw consent. To exercise rights, contact us at the email above. You may also complain to the ICO at ico.org.uk.",
  cookiesNote: "We use essential cookies to deliver the site and, with consent, analytics cookies. See our Cookie Policy for details.",
  childrenNote: "Our services are not directed at children under 13 and we do not knowingly collect their data.",
  changesNote: "We may update this notice. Material changes will be communicated by email or prominent notice at least 14 days before they take effect.",
});

export const defaultCookiePolicy = () => ({
  websiteName: "Northstar Studio",
  websiteUrl: "https://northstar.example.co.uk",
  contactEmail: "privacy@example.co.uk",
  effectiveDate: new Date().toISOString().slice(0, 10),
  consentMechanism: "On first visit you see a banner where you can Accept all, Reject all, or manage preferences. You can change your choice anytime via the Cookie Settings link in the footer.",
  cookies: [
    { id: "ck-1", name: "session", category: "Strictly necessary", purpose: "Maintains your login session.", retention: "Session" },
    { id: "ck-2", name: "csrf_token", category: "Strictly necessary", purpose: "Prevents cross-site request forgery.", retention: "Session" },
    { id: "ck-3", name: "_ga", category: "Analytics", purpose: "Google Analytics — measures page views and user journeys.", retention: "13 months" },
    { id: "ck-4", name: "_gid", category: "Analytics", purpose: "Google Analytics — distinguishes users.", retention: "24 hours" },
    { id: "ck-5", name: "marketing_consent", category: "Marketing", purpose: "Records marketing consent so we don't ask again.", retention: "12 months" },
  ],
});

export const defaultTermsOfService = () => ({
  companyName: "Northstar Studio Ltd",
  companyNumber: "12345678",
  companyAddress: "1 Practical Lane, London EC1A 1AA",
  websiteUrl: "https://northstar.example.co.uk",
  contactEmail: "hello@example.co.uk",
  effectiveDate: new Date().toISOString().slice(0, 10),
  serviceDescription: "Northstar Studio provides web-based business planning, document generation, and template tools to small businesses and freelancers in the United Kingdom.",
  acceptance: "By accessing or using the Service you agree to these Terms. If you do not agree, do not use the Service.",
  accountTerms: "You must provide accurate registration details, keep your password confidential, and notify us of any unauthorised account use. You are responsible for all activity under your account.",
  payment: "Paid plans are billed monthly or annually in advance. We do not refund partial months unless required by UK consumer law. Prices may change with 30 days' written notice for ongoing plans.",
  ip: "All Service content, software, branding, and templates are owned by Northstar Studio Ltd or its licensors. You retain ownership of content you submit, but grant us a non-exclusive licence to host and display it for the purpose of delivering the Service.",
  acceptableUse: "You must not use the Service to break the law, infringe rights, distribute malware, scrape automated content, or interfere with the Service's operation.",
  termination: "We may suspend or terminate your account for material breach with reasonable notice. You may cancel anytime; we'll close your account at the end of the paid period.",
  liability: "To the fullest extent permitted by law, our total liability is capped at the fees you paid in the 12 months before the claim. We exclude indirect and consequential losses. Nothing limits liability for death, personal injury caused by negligence, or fraud.",
  warranties: "The Service is provided 'as is'. We do not guarantee uninterrupted or error-free operation but will use reasonable care to maintain availability.",
  indemnity: "You agree to indemnify us against third-party claims arising from your unlawful use of the Service or breach of these Terms.",
  governingLaw: "These Terms are governed by the laws of England and Wales. Disputes are subject to the exclusive jurisdiction of the courts of England and Wales.",
  contactNotice: "Notices may be sent to the contact email above. We may update these Terms from time to time; material changes will be communicated at least 14 days before they take effect.",
});

export const defaultDpa = () => ({
  controllerName: "Customer Ltd",
  controllerAddress: "Customer registered office",
  processorName: "Northstar Studio Ltd",
  processorAddress: "1 Practical Lane, London EC1A 1AA",
  effectiveDate: new Date().toISOString().slice(0, 10),
  subject: "Processing of personal data necessary for the Processor to provide the Northstar Studio service to the Controller, as set out in the underlying Service Agreement.",
  duration: "For the term of the Service Agreement and for any period of post-termination assistance required.",
  nature: "Hosting, storing, transmitting, and processing personal data to deliver the agreed services, including authentication, document storage, support, and analytics.",
  purpose: "To enable the Controller to use the Service in line with the Service Agreement and provide support and incident response.",
  dataCategories: "Identification data (name, email), contact data, usage data, support correspondence, and any personal data the Controller submits to the Service.",
  dataSubjects: "Controller's employees, contractors, and end customers whose personal data is submitted to the Service.",
  subProcessors: "AWS (hosting, EU/UK regions), Stripe (payments), Postmark (transactional email), Sentry (error monitoring).",
  securityMeasures: "Encryption at rest (AES-256) and in transit (TLS 1.2+), least-privilege access controls, MFA on admin accounts, automated backups with 30-day retention, annual penetration testing, and documented incident response plan.",
  breachNotification: "The Processor will notify the Controller without undue delay and within 72 hours of becoming aware of a personal data breach affecting the Controller's data.",
  internationalTransfers: "Where personal data is transferred outside the UK or EEA, the parties will use the UK International Data Transfer Agreement or applicable Standard Contractual Clauses.",
  audits: "The Controller may audit the Processor's compliance once per year on 30 days' written notice, during business hours, at the Controller's cost.",
  returnOrDeletion: "Upon termination of the Service Agreement, the Processor will, at the Controller's choice, return or delete all personal data within 30 days, save where retention is required by law.",
  governingLaw: "England and Wales",
  signerControllerName: "Controller Signer",
  signerControllerTitle: "Director",
  signerProcessorName: "Founder Name",
  signerProcessorTitle: "Director",
});
